// ────────────────────────────────────────────────────────────────
//  Deskte — File Actions
//
//  <FileActions file={{name, data, type}} lang />   — buttons (👁 ⬇ 🖨)
//  <FilePreviewModal ... />                         — inline preview
//
//  Works with any file stored as a base64 data URL. Supports PDFs,
//  images, text and falls back to "download only" for everything else.
// ────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { Eye, Download, Printer, X, Send } from "lucide-react";

// ─── Helpers ────────────────────────────────────────────────────
export const fileIconFor = (type) => {
  if (!type) return "📄";
  if (type.startsWith("image/")) return "🖼️";
  if (type === "application/pdf") return "📕";
  if (type.includes("word")) return "📘";
  if (type.includes("excel") || type.includes("sheet")) return "📗";
  if (type.startsWith("text/")) return "📝";
  return "📄";
};

export const formatBytes = (bytes) => {
  if (bytes == null) return "";
  if (typeof bytes === "string") return bytes;
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / 1024 / 1024).toFixed(1) + " MB";
};

/**
 * Open the user's email client with a pre-filled message. Because browsers
 * cannot attach files via mailto:, we ask the user to attach it manually
 * after the draft opens. Supports an optional default recipient.
 */
export const sendFile = (file, { to = "", subject = "", body = "" } = {}) => {
  if (!file) return;
  const subj = subject || `File: ${file.name || "document"}`;
  const bd = body ||
    `Hi,\n\nPlease find the document "${file.name || "attached file"}" attached.\n\n— Sent from Deskte CRM`;
  // Download the file first so the user has it ready to attach
  downloadFile(file);
  const href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(bd)}`;
  // Open mailto in a tiny timeout so the download kicks in first
  setTimeout(() => { window.location.href = href; }, 120);
};

/** Trigger a download in the browser. */
export const downloadFile = (file) => {
  if (!file || !file.data) return;
  const a = document.createElement("a");
  a.href = file.data;
  a.download = file.name || "download";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

/**
 * Print a file using a hidden iframe. Works for PDFs and images.
 * Falls back to opening in a new window for other types.
 */
export const printFile = (file) => {
  if (!file || !file.data) return;
  const type = file.type || "";
  if (type === "application/pdf" || type.startsWith("image/")) {
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    // For images, wrap in an HTML document so print dialog renders cleanly
    if (type.startsWith("image/")) {
      const html = `<!doctype html><html><head><title>${file.name || "Print"}</title><style>
        html,body{margin:0;padding:0;background:#fff;}
        img{max-width:100%;height:auto;display:block;margin:0 auto;}
        @media print { @page { margin: 0; } body { margin: 10mm; } }
      </style></head><body><img src="${file.data}" onload="setTimeout(()=>{window.focus();window.print();},120)"/></body></html>`;
      iframe.srcdoc = html;
    } else {
      // PDFs from data: URLs fail in iframes on Chrome — use a blob URL.
      const src = file.data.startsWith("data:")
        ? (() => {
            try {
              const m = /^data:([^;,]+)(;base64)?,(.*)$/i.exec(file.data);
              if (!m) return file.data;
              const mime = m[1];
              const bin = atob(m[3]);
              const bytes = new Uint8Array(bin.length);
              for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
              return URL.createObjectURL(new Blob([bytes], { type: mime }));
            } catch { return file.data; }
          })()
        : file.data;
      iframe.src = src;
      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (e) {
          window.open(src, "_blank", "noopener,noreferrer");
        }
      };
    }
    document.body.appendChild(iframe);
    // Clean up after a minute
    setTimeout(() => {
      try { document.body.removeChild(iframe); } catch {}
    }, 60000);
  } else {
    // Unknown types — open in a new tab so the user can print from there
    const w = window.open(file.data, "_blank");
    if (w) {
      setTimeout(() => { try { w.focus(); w.print(); } catch {} }, 400);
    }
  }
};

// Convert a data: URL to a Blob URL. Chrome blocks rendering PDFs from
// data: URLs inside <iframe>, so we turn them into blob: URLs which work.
const dataUrlToBlobUrl = (dataUrl) => {
  try {
    const m = /^data:([^;,]+)(;base64)?,(.*)$/i.exec(dataUrl || "");
    if (!m) return dataUrl;
    const mime = m[1] || "application/octet-stream";
    const isB64 = !!m[2];
    const payload = m[3] || "";
    let bytes;
    if (isB64) {
      const bin = atob(payload);
      bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    } else {
      const dec = decodeURIComponent(payload);
      bytes = new Uint8Array(dec.length);
      for (let i = 0; i < dec.length; i++) bytes[i] = dec.charCodeAt(i);
    }
    return URL.createObjectURL(new Blob([bytes], { type: mime }));
  } catch {
    return dataUrl;
  }
};

// ─── Preview modal ──────────────────────────────────────────────
export const FilePreviewModal = ({ file, lang = "en", onClose }) => {
  const type = file?.type || "";
  const es = lang === "es";

  const isPdf = type === "application/pdf" || /\.pdf($|\?)/i.test(file?.name || "");
  const isImage = type.startsWith("image/");
  const isText =
    type.startsWith("text/") ||
    /\.(txt|csv|md|log|json)$/i.test(file?.name || "");

  // PDFs must be served from a blob: URL — Chrome blocks data: URLs in iframes.
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  useEffect(() => {
    if (!isPdf || !file?.data) { setPdfBlobUrl(null); return; }
    if (file.data.startsWith("blob:") || file.data.startsWith("http")) {
      setPdfBlobUrl(file.data);
      return;
    }
    const url = dataUrlToBlobUrl(file.data);
    setPdfBlobUrl(url);
    return () => {
      if (url && url.startsWith("blob:")) {
        try { URL.revokeObjectURL(url); } catch {}
      }
    };
  }, [isPdf, file?.data]);

  if (!file) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 500,
        background: "rgba(0,0,0,.85)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0d1c2e",
          border: "1px solid #1a3550",
          borderRadius: 14,
          width: "100%",
          maxWidth: 980,
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 30px 100px rgba(0,0,0,.7)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: ".75rem 1rem",
            borderBottom: "1px solid #1a3550",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: ".5rem",
              minWidth: 0,
              flex: 1,
            }}
          >
            <span style={{ fontSize: "1.1rem" }}>{fileIconFor(type)}</span>
            <span
              style={{
                fontSize: ".86rem",
                fontWeight: 700,
                color: "#eef6ff",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {file.name || (es ? "Archivo" : "File")}
            </span>
            {file.size && (
              <span
                style={{
                  fontSize: ".68rem",
                  color: "#45688a",
                  fontFamily: "var(--font-mono,monospace)",
                  flexShrink: 0,
                }}
              >
                {formatBytes(file.size)}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: ".35rem", flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => downloadFile(file)}
              title={es ? "Descargar" : "Download"}
              style={{
                padding: ".4rem .7rem",
                borderRadius: 7,
                border: "1px solid rgba(0,212,255,.35)",
                background: "rgba(0,212,255,.1)",
                color: "#00d4ff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: ".74rem",
                fontWeight: 700,
                fontFamily: "inherit",
              }}
            >
              <Download size={13} /> {es ? "Descargar" : "Download"}
            </button>
            <button
              type="button"
              onClick={() => printFile(file)}
              title={es ? "Imprimir" : "Print"}
              style={{
                padding: ".4rem .7rem",
                borderRadius: 7,
                border: "1px solid rgba(0,255,136,.35)",
                background: "rgba(0,255,136,.1)",
                color: "#00ff88",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: ".74rem",
                fontWeight: 700,
                fontFamily: "inherit",
              }}
            >
              <Printer size={13} /> {es ? "Imprimir" : "Print"}
            </button>
            <button
              type="button"
              onClick={() => sendFile(file)}
              title={es ? "Enviar por email" : "Send by email"}
              style={{
                padding: ".4rem .7rem",
                borderRadius: 7,
                border: "1px solid rgba(251,146,60,.35)",
                background: "rgba(251,146,60,.1)",
                color: "#fb923c",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: ".74rem",
                fontWeight: 700,
                fontFamily: "inherit",
              }}
            >
              <Send size={13} /> {es ? "Enviar" : "Send"}
            </button>
            <button
              type="button"
              onClick={onClose}
              title={es ? "Cerrar" : "Close"}
              style={{
                padding: ".4rem .5rem",
                borderRadius: 7,
                border: "1px solid #1a3550",
                background: "#112538",
                color: "#7aa3c0",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                fontFamily: "inherit",
              }}
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            background: "#070d14",
            overflow: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isPdf ? 0 : "1.25rem",
          }}
        >
          {isPdf && pdfBlobUrl && (
            <iframe
              src={pdfBlobUrl}
              title={file.name || "PDF"}
              style={{
                width: "100%",
                height: "78vh",
                border: "none",
                background: "#fff",
              }}
            />
          )}
          {isPdf && !pdfBlobUrl && (
            <div style={{ color: "#7aa3c0", fontSize: ".85rem", padding: "2rem" }}>
              {es ? "Cargando vista previa..." : "Loading preview..."}
            </div>
          )}
          {isImage && (
            <img
              src={file.data}
              alt={file.name || ""}
              style={{
                maxWidth: "100%",
                maxHeight: "78vh",
                objectFit: "contain",
                borderRadius: 6,
              }}
            />
          )}
          {isText && <TextPreview file={file} />}
          {!isPdf && !isImage && !isText && (
            <div
              style={{
                textAlign: "center",
                color: "#7aa3c0",
                fontSize: ".85rem",
                padding: "2.5rem 1rem",
              }}
            >
              <div style={{ fontSize: "3rem", marginBottom: ".6rem" }}>
                {fileIconFor(type)}
              </div>
              <div style={{ marginBottom: ".4rem", color: "#eef6ff", fontWeight: 700 }}>
                {es
                  ? "Vista previa no disponible para este formato"
                  : "Preview not available for this format"}
              </div>
              <div style={{ fontSize: ".75rem", color: "#45688a" }}>
                {es
                  ? "Usa Descargar o Imprimir para abrirlo con el programa adecuado."
                  : "Use Download or Print to open it in the right app."}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TextPreview = ({ file }) => {
  const [text, setText] = useState(null);
  if (text === null) {
    try {
      // Decode base64 part of the data URL
      const idx = (file.data || "").indexOf("base64,");
      if (idx >= 0) {
        const b64 = file.data.slice(idx + 7);
        const bin = atob(b64);
        setText(bin);
      } else {
        setText("");
      }
    } catch {
      setText("");
    }
  }
  return (
    <pre
      style={{
        width: "100%",
        maxHeight: "78vh",
        overflow: "auto",
        background: "#0d1c2e",
        color: "#c8dff0",
        padding: "1rem",
        borderRadius: 8,
        fontSize: ".78rem",
        fontFamily: "var(--font-mono,monospace)",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        margin: 0,
      }}
    >
      {text}
    </pre>
  );
};

// ─── FileActions buttons (inline, for list rows) ────────────────
export const FileActions = ({ file, lang = "en", size = "sm", sendTo = "", sendSubject = "", sendBody = "", showSend = true }) => {
  const [preview, setPreview] = useState(false);
  if (!file || !file.data) return null;
  const es = lang === "es";

  // For PDFs, open a new tab with a blob URL — Chrome's native PDF viewer
  // renders it full-screen and works everywhere (production + Electron).
  // For images/text we still use the in-app modal.
  const isPdf = (file.type === "application/pdf") || /\.pdf($|\?)/i.test(file.name || "");
  const handleView = () => {
    if (!isPdf) { setPreview(true); return; }
    try {
      const m = /^data:([^;,]+)(;base64)?,(.*)$/i.exec(file.data || "");
      let url = file.data;
      if (m && m[2]) {
        const bin = atob(m[3]);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        url = URL.createObjectURL(new Blob([bytes], { type: m[1] }));
      } else if (file.data?.startsWith?.("blob:") || file.data?.startsWith?.("http")) {
        url = file.data;
      }
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) setPreview(true); // popup blocked — fall back to modal
    } catch {
      setPreview(true);
    }
  };

  const sz = size === "lg" ? 15 : 12;
  const pad = size === "lg" ? ".35rem .6rem" : ".22rem .5rem";
  const fontSize = size === "lg" ? ".75rem" : ".68rem";

  const btnBase = {
    padding: pad,
    borderRadius: 6,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    fontSize,
    fontWeight: 700,
    fontFamily: "inherit",
    transition: "background .15s",
  };

  return (
    <>
      <div style={{ display: "inline-flex", gap: 4 }}>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleView(); }}
          title={es ? "Ver" : "View"}
          style={{
            ...btnBase,
            border: "1px solid rgba(0,212,255,.35)",
            background: "rgba(0,212,255,.08)",
            color: "#00d4ff",
          }}
        >
          <Eye size={sz} />
        </button>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); downloadFile(file); }}
          title={es ? "Descargar" : "Download"}
          style={{
            ...btnBase,
            border: "1px solid rgba(129,140,248,.35)",
            background: "rgba(129,140,248,.08)",
            color: "#818cf8",
          }}
        >
          <Download size={sz} />
        </button>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); printFile(file); }}
          title={es ? "Imprimir" : "Print"}
          style={{
            ...btnBase,
            border: "1px solid rgba(0,255,136,.35)",
            background: "rgba(0,255,136,.08)",
            color: "#00ff88",
          }}
        >
          <Printer size={sz} />
        </button>
        {showSend && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); sendFile(file, { to: sendTo, subject: sendSubject, body: sendBody }); }}
            title={es ? "Enviar por email" : "Send by email"}
            style={{
              ...btnBase,
              border: "1px solid rgba(251,146,60,.35)",
              background: "rgba(251,146,60,.08)",
              color: "#fb923c",
            }}
          >
            <Send size={sz} />
          </button>
        )}
      </div>
      {preview && (
        <FilePreviewModal file={file} lang={lang} onClose={() => setPreview(false)} />
      )}
    </>
  );
};
